import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { setUnauthorizedHandler } from '@/common/services/request';
import { authClient } from '@/common/utils/auth-client';
import { useOrganizationPath } from './use-organization-path';

const AppContext = createContext(null);

const fetchCurrentTabOrganizationRole = async ({ organizationId, userId }) => {
  if (!organizationId || !userId) {
    return undefined;
  }

  const { data, error } = await authClient.organization.listMembers({
    query: {
      organizationId,
      limit: 1,
      filterField: 'userId',
      filterOperator: 'eq',
      filterValue: userId,
    },
  });
  if (error) {
    return undefined;
  }
  return data?.members?.[0]?.role;
};

const createAnonymousSession = () => ({
  role: undefined,
  user: undefined,
  status: 'anonymous',
});

const createAuthenticatedSession = (user, role) => ({
  // role 是当前 Tab 的 URL organizationId 作用域内，组织为当前 user 设置的角色；
  // 它不是 user 的全局属性，避免跨 Tab 切换组织时发生 active organization 串扰。
  role,
  user,
  status: 'authenticated',
});

const createLoadingSession = () => ({
  role: undefined,
  user: undefined,
  status: 'loading',
});

export const AppStateProvider = ({ children }) => {
  const location = useLocation();
  const { organizationId } = useOrganizationPath();
  const hasValidatedRef = useRef(false);
  const initialSession = createLoadingSession();
  const sessionRef = useRef(initialSession);
  const [sessionState, setSessionState] = useState(initialSession);

  const commitSession = useCallback((session) => {
    sessionRef.current = session;
    setSessionState(session);
  }, []);

  const commitUserSession = useCallback(
    (user, role) => {
      commitSession(user ? createAuthenticatedSession(user, role) : createAnonymousSession());
    },
    [commitSession],
  );

  const refreshSession = useCallback(
    async (options = {}) => {
      const { background = false } = options;
      if (!background && sessionRef.current.status !== 'authenticated') {
        commitSession(createLoadingSession());
      }
      try {
        const response = await authClient.getSession();
        const user = response?.data?.user;
        const role = await fetchCurrentTabOrganizationRole({
          organizationId,
          userId: user?.id,
        });
        commitUserSession(user, role);
        return user;
      } catch {
        if (background && sessionRef.current.status === 'authenticated') {
          return sessionRef.current.user;
        }
        commitSession(createAnonymousSession());
        return undefined;
      }
    },
    [commitSession, commitUserSession, organizationId],
  );

  const logout = useCallback(async () => {
    await authClient.signOut();
    commitSession(createAnonymousSession());
  }, [commitSession]);

  useEffect(() => {
    setUnauthorizedHandler(async () => {
      await logout();
    });
    return () => setUnauthorizedHandler(undefined);
  }, [logout]);

  useEffect(() => {
    const background = hasValidatedRef.current;
    hasValidatedRef.current = true;
    refreshSession({
      background,
    });
  }, [location.pathname, refreshSession]);

  const value = useMemo(
    () => ({
      session: {
        ...sessionState,
        refresh: refreshSession,
        logout,
      },
    }),
    [sessionState, refreshSession, logout],
  );
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used inside AppStateProvider');
  }
  return context;
};
